import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ManagedDepartmentsSearchFilterQuery } from "models/filters/managedDepartments/ManagedDepartmentsSearchFilterQuery";
import { ManagedDepartmentsApi } from "apis/ManagedDepartmentsApi";
import { ManagedDepartment } from "models/dbModels/managedDepartments/ManagedDepartment";
import { ManagedStocksApi } from "apis/ManagedStocksApi";
import { ManagedStockSearchFilterQuery } from "models/filters/managedStocks/ManagedStockSearchFilterQuery";
import { ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { ManagedSubdepartment } from "models/dbModels/managedSubdepartments/ManagedSubdepartment";
import { ManagedSubdepartmentsApi } from "apis/ManagedSubdepartmentsApi";
import { ManagedSubdepartmentsSearchFilterQuery } from "models/filters/managedSubDepartments/ManagedSubdepartmentsSearchFilterQuery";
import { BulkEditManagedStock } from "models/daoModels/managedStock/BulkEditManagedStock";

export const getDepartments = createAsyncThunk("stockDepartmentPageSlice/getDepartments", async (params?: ManagedDepartmentsSearchFilterQuery) => {
    const response = await ManagedDepartmentsApi.Search(params ? { ...params } : {});
    return response.data;
});

export const getDepartmentDetails = createAsyncThunk("stockDepartmentPageSlice/getDepartmentDetails", async (departmentIds: number[]) => {
    const response = await ManagedDepartmentsApi.Search({ ids: departmentIds });
    return response.data;
});

export const deleteDepartment = createAsyncThunk("stockDepartmentPageSlice/deleteDepartment", async (params: ManagedDepartmentsSearchFilterQuery) => {
    const response = await ManagedDepartmentsApi.Delete(params);
    return response.data;
});

export const createDepartment = createAsyncThunk("stockDepartmentPageSlice/updateDepartment", async (department: ManagedDepartment) => {
    const response = await ManagedDepartmentsApi.Add([department]);
    return response.data;
});

export const createDepartments = createAsyncThunk("stockDepartmentPageSlice/createDepartments", async (departments: ManagedDepartment[]) => {
    const response = await ManagedDepartmentsApi.Add(departments);
    return response.data;
});

export const updateDepartment = createAsyncThunk("stockDepartmentPageSlice/updateDepartment", async (department: ManagedDepartment) => {
    const response = await ManagedDepartmentsApi.Update([department]);
    return response.data;
});

export const updateDepartments = createAsyncThunk("stockDepartmentPageSlice/updateDepartments", async (departments: ManagedDepartment[]) => {
    const response = await ManagedDepartmentsApi.Update(departments);
    return response.data;
});

export const getDepartmentStocks = createAsyncThunk("stockDepartmentPageSlice/getStocks", async (params: ManagedStockSearchFilterQuery) => {
    const response = await ManagedStocksApi.Search(params);
    return response.data;
});

export const getSubDepartments = createAsyncThunk("stockDepartmentPageSlice/getSubDepartments", async (departmentId?: number) => {
    const response = await ManagedSubdepartmentsApi.Search(departmentId ? { departmentIds: [departmentId] } : {});
    return response.data;
});

export const deleteSubDepartment = createAsyncThunk("stockDepartmentPageSlice/deleteSubDepartment", async (params: ManagedSubdepartmentsSearchFilterQuery) => {
    const response = await ManagedSubdepartmentsApi.Delete(params);
    return response.data;
});

export const importStockToSubdepartment = createAsyncThunk("stockDepartmentPageSlice/importStockToSubdepartment", async (params: BulkEditManagedStock) => {
    const response = await ManagedStocksApi.BulkUpdate(params);
    return response.data;
});

export const deleteStocksFromSubdepartment = createAsyncThunk("stockDepartmentPageSlice/deleteStocksFromSubdepartment", async (params: BulkEditManagedStock) => {
    const response = await ManagedStocksApi.BulkUpdate(params);
    return response.data;
});

export const deleteStocksFromDepartment = createAsyncThunk("stockDepartmentPageSlice/deleteStocksFromDepartment", async (params: BulkEditManagedStock) => {
    const response = await ManagedStocksApi.BulkUpdate(params);
    return response.data;
});

type InitialStateType = {
    id: number;
    name: string;
    number: string;
    lock: boolean;
    modelId: string;
    lastModified: string;
    subdepartmentsTable: any[];
    loading: boolean;
    selectedRows: any[];//{ position: number, id: number }[][];
    stockPopup: boolean;
    bulkEditPopup: boolean;
    selectSubDepartmentPopup: boolean;
    selectedSubDepartment: number;
    error: string;
    table: ManagedDepartment[];
    subDepartments: ManagedSubdepartment[];
    showSubdepartmentPopup: boolean;
    openSubDeparmentId: number;
    openSubDeparmentDepartmentId: number;
    selectedSubDepartmentIds: number[],
    loadStocks: boolean;
};

const initialState : InitialStateType = {
    id: 0,
    name: "",
    number: "",
    lock: false,
    modelId: "",
    lastModified: "",
    subdepartmentsTable: [],
    loading: false,
    selectedRows: [],
    stockPopup: false,
    bulkEditPopup: false,
    selectSubDepartmentPopup: false,
    selectedSubDepartment: 0,
    error: "",
    table: [],
    subDepartments: [],
    showSubdepartmentPopup: false,
    openSubDeparmentId: 0,
    openSubDeparmentDepartmentId: 0,
    selectedSubDepartmentIds: [],
    loadStocks: false
};

type StocksTableGroupType = {
    stocksTable: ManagedStockView[];
    loadingStocks: boolean;
    stocksKeyword: string;
    totalRows: number;
    totalPages: number;
    pageIndex: number;
    pageSize: number;
    selectedStockRows: any[];
    deselectedStockRows: any[];
    selectAllFilter: boolean;
};

const stocksTableGroup : StocksTableGroupType = {
    stocksTable: [],
    loadingStocks: false,
    stocksKeyword: "",
    totalRows: 0,
    totalPages: 0,
    pageIndex: 0,
    pageSize: 100,
    selectedStockRows: [],
    deselectedStockRows: [],
    selectAllFilter: false,
}

const departmentsSlice = createSlice({
    name: "stockDepartmentPageSlice",
    initialState: {
        ...initialState,
        ...stocksTableGroup,
    },
    reducers: {
        updateDepartmentDetails: (state, action) => {
            return {
                ...state,
                id: action.payload.id,
                name: action.payload.name,
                lock: action.payload.lock,
                lastModified: action.payload.lastModified,
                storeGroupId: action.payload.storeGroupId,
                subdepartmentsTable: action.payload.subdepartmentsTable
            };
        },
        updateDepartmentField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        },
        cleanDepartmentDetails: (state) => {
            return {
                ...state,
                ...initialState,
                ...stocksTableGroup,
            };
        },
        addDepartmentSubdepartments: (state, action) => {
            return {
                ...state,
                subdepartmentsTable: state.subdepartmentsTable.concat(action.payload),
            };
        },
        addSelectedRows: (state, action) => {
            // @ts-ignore
            state.selectedStockRows[action.payload.index] = action.payload.rows;
        },
        addDeselectedRows: (state, action) => {
            // @ts-ignore
            state.deselectedStockRows[action.payload.index] = action.payload.rows;
        },
        cleanSubDepartmentDetails: (state) => {
            return {
                ...state,
                subdepartmentsTable: []
            };
        }
    },
    extraReducers(builder) {
        builder.addCase(getDepartments.pending, (state, action) => {
            state.loading = true;
            state.table = [];
            state.error = "";
        });
        builder.addCase(getDepartmentDetails.fulfilled, (state, action) => {
            state.id = action.payload.data[0].id;
            state.name = action.payload.data[0].name;
            state.lock = action.payload.data[0].lock;
            state.number = action.payload.data[0].number;
        });
        builder.addCase(getDepartments.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        });
        builder.addCase(getDepartments.fulfilled, (state, action) => {
            state.loading = false;
            state.table = action.payload.data ? action.payload.data : [];
        });
        builder.addCase(getDepartmentStocks.fulfilled, (state, action) => {
            state.stocksTable = action.payload.data;
            state.totalRows = action.payload.totalRows;
            state.totalPages = Math.ceil(Math.ceil(action.payload.totalRows) / state.pageSize);
            state.loading = false;
        });
        builder.addCase(getDepartmentStocks.pending, (state, action) => {
            state.loading = true;
            state.stocksTable = [];
            state.error = "";
        });
        builder.addCase(getDepartmentStocks.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        });
        builder.addCase(getSubDepartments.fulfilled, (state, action) => {
            state.subDepartments = action.payload.data;
        });
        builder.addCase(getSubDepartments.rejected, (state, action) => {
            state.subDepartments = [];
        });
    }
});

export const {
    updateDepartmentDetails,
    cleanDepartmentDetails,
    updateDepartmentField,
    addDepartmentSubdepartments,
    addSelectedRows,
    addDeselectedRows,
    cleanSubDepartmentDetails,
} = departmentsSlice.actions;

export default departmentsSlice.reducer;